import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { DateInput } from '@/common/components/DateInput/DateInput'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import ResumeItem from '@/common/components/ResumeItem'
import { ShowHeader } from '@/common/components/ShowHeader'
import TextField from '@/common/components/TextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { formatCurrency } from '@/common/utils/formatters'
import { FormTransformers } from '@/common/utils/formTransformers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ClockIcon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { MaintenanceForwardingDirectiveEntryMap } from '@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type'
import { MaintenanceForwardingData } from '@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type'
import { MaintenanceForwardingsDirectiveEntryKlass } from '@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type'
import { useUpdateMaintenanceExecutionEntryEstablishmentAccept } from '@/features/maintenance/features/workflows/features/executionEntry/hooks/useUpdateMaintenanceExecutionEntryEstablishmentAccept'
import {
  updateExecutionEntryEstablishmentAcceptSchema,
  UpdateExecutionEntryEstablishmentAcceptValidationSchema,
} from '@/features/maintenance/features/workflows/features/executionEntry/schemas/updateExecutionEntryEstablishmentAccept.schema'
import { updateExecutionEntryErrorsTransformer } from '@/features/maintenance/features/workflows/features/executionEntry/utils/updateExecutionEntryErrorsTransformer'
import MaintenanceWorkflowExecutionEntryUpdateLoading from '@/features/maintenance/features/workflows/features/executionEntry/screens/Update/components/MaintenanceWorkflowExecutionEntryUpdateLoading'
import ExecutionEntryUpdateObjectiveAttributes from '@/features/maintenance/features/workflows/features/executionEntry/screens/Update/components/sections/ObjectiveAttributes'
import { useMaintenanceManualEntryProposal } from '../../hooks/useMaintenanceManualEntryProposal'

type ExecutionForwarding = MaintenanceForwardingData<
  MaintenanceForwardingDirectiveEntryMap[MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry]
>

const MaintenanceProposalsCreateExecutionProposal = () => {
  const { id } = useParams()

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const queryClient = useQueryClient()

  const navigate = useNavigate()

  const [formDisabled, setFormDisabled] = useState(false)

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    data: proposal,
    isPending: proposalIsPending,
    error: proposalError = {} as any,
    refetch: refetchProposal,
    dataUpdatedAt: proposalUpdatedAt,
  } = useMaintenanceManualEntryProposal({ proposalId: id })

  const directiveEntry = proposal?.manual_entry?.forwarding?.directive_entry

  const objectivesForwarding = useMemo(
    () => ({ directive_entry: { objectives: directiveEntry?.objectives ?? [] } }) as unknown as ExecutionForwarding,
    [proposalUpdatedAt],
  )

  const formMethods = useForm<UpdateExecutionEntryEstablishmentAcceptValidationSchema>({
    resolver: zodResolver(updateExecutionEntryEstablishmentAcceptSchema),
    disabled: formDisabled,
  })

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = formMethods

  const { mutate: establishmentAccept, isPending: establishmentAcceptIsPending } =
    useUpdateMaintenanceExecutionEntryEstablishmentAccept({
      establishmentChoiceEntryId: proposal?.id,
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['maintenanceManualEntryProposals'] })
          queryClient.invalidateQueries({ queryKey: ['maintenanceManualEntryProposal', id] })

          navigate(`/maintenance-proposals/${id}`, { replace: true })
        },
        onError: (error) => {
          FormTransformers.errorsTransformer({
            setError,
            setMessages,
            transformer: updateExecutionEntryErrorsTransformer,
          })(error)
        },
      },
    })

  const onSubmit: SubmitHandler<UpdateExecutionEntryEstablishmentAcceptValidationSchema> = (data) =>
    establishmentAccept(data)

  const isLoading = isUserPermissionsPending || proposalIsPending

  const hasPermissionToUpdate = useMemo(() => {
    return hasUserPermission(
      'Establishment::Maintenance::EstablishmentChoice::ManualEntryProposals::EstablishmentAccept',
    )
  }, [isUserPermissionsPending])

  useEffect(() => {
    if (proposalIsPending) {
      return
    }

    const pieces = directiveEntry?.objectives?.flatMap(
      (objective) =>
        objective.execution_pieces?.map((piece) => ({
          id: piece.id,
          original_unit_price: String(piece.unit_price),
          original_warranty_in_days: piece.warranty_in_days,
          new_unit_price: undefined,
          new_warranty_in_days: undefined,
        })) || [],
    )

    const activities = directiveEntry?.objectives?.flatMap(
      (objective) =>
        objective.execution_activities?.map((activity) => ({
          id: activity.id,
          original_unit_price: String(activity.unit_price),
          original_warranty_in_days: activity.warranty_in_days,
          new_unit_price: undefined,
          new_warranty_in_days: undefined,
        })) || [],
    )

    reset({
      start_at_date: undefined,
      start_at_time: undefined,
      deadline_to_complete_in_days: undefined,
      pieces,
      activities,
    })
  }, [proposalUpdatedAt])

  useEffect(() => {
    setFormDisabled(establishmentAcceptIsPending)
  }, [establishmentAcceptIsPending])

  return (
    <FormProvider {...formMethods}>
      <div className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/maintenance-proposals/${id}`, searchLastRouteFromFallback: true }}
          isLoading={isLoading}
          title="Lance para execução da manutenção"
          titleClassName="text-lg font-medium"
          buttons={
            !isLoading &&
            hasPermissionToUpdate && (
              <Button onClick={handleSubmit(onSubmit)} isLoading={establishmentAcceptIsPending}>
                Enviar lance para execução
              </Button>
            )
          }
        />

        {isLoading && <MaintenanceWorkflowExecutionEntryUpdateLoading />}

        {!isLoading && (
          <ErrorMessageWrapper
            className="flex-1"
            statusCode={!hasPermissionToUpdate ? 403 : proposalError?.status}
            refetchFunction={refetchProposal}
          >
            <div className="flex-1 overflow-auto">
              <Box className="p-4 flex flex-col gap-4">
                <p className="text-lg font-medium">Edite os orçamentos por Objeto de manutenção</p>

                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium">Dados do diagnóstico</p>

                  <div className="flex gap-3 pb-3 border-b border-white-200">
                    <ResumeItem
                      className="text-sm"
                      label="Valor total"
                      value={directiveEntry?.total_value != null ? formatCurrency(Number(directiveEntry.total_value)) : '--'}
                    />

                    <ResumeItem
                      className="text-sm"
                      label="Prazo em dias"
                      value={
                        directiveEntry?.deadline_to_complete_in_days != null
                          ? `${directiveEntry.deadline_to_complete_in_days} dia(s)`
                          : '--'
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <DateInput
                    fieldName="start_at_date"
                    control={control}
                    label="Data sugerida"
                    className="flex-1"
                    markAsRequired
                    errorMessage={errors.start_at_date?.message}
                  />

                  <Controller
                    control={control}
                    name="start_at_time"
                    render={({ field }) => (
                      <TextField
                        label="Horário sugerido"
                        type="time"
                        markAsRequired
                        className="flex-1"
                        LeftIcon={<ClockIcon className="w-5 h-5 text-white-400" />}
                        {...field}
                        value={field.value || ''}
                        errorMessage={errors.start_at_time?.message}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="deadline_to_complete_in_days"
                    render={({ field }) => (
                      <UnitTextField
                        decimals={0}
                        placeholder="Escreva aqui o prazo em dias"
                        unit="dia(s)"
                        label="Prazo em dias"
                        markAsRequired
                        {...field}
                        errorMessage={errors.deadline_to_complete_in_days?.message}
                      />
                    )}
                  />
                </div>

                <ExecutionEntryUpdateObjectiveAttributes
                  forwarding={objectivesForwarding}
                  dataUpdatedAt={proposalUpdatedAt}
                />
              </Box>
            </div>
          </ErrorMessageWrapper>
        )}

        <ModalMessageSimple messages={messages} setMessages={setMessages} isError />
      </div>
    </FormProvider>
  )
}

export default MaintenanceProposalsCreateExecutionProposal
